export const projects = [
  {
    title: "Support Intake Automation System",
    subtitle:
      "Azure Functions + Cosmos DB + WhatsApp Business API | Event-Driven Customer Support Backend",
    summary:
      "Serverless backend for an automated WhatsApp-based customer support intake system that replaces manual triage with a deterministic, state-machine–driven conversational workflow. Incoming WhatsApp messages trigger Azure Functions that load and persist user sessions in Cosmos DB, enforce global rules like cooldowns and escalation triggers, and drive users through states such as WELCOME, AWAITING_MENU_SELECTION, AWAITING_EMAIL, and ESCALATED_TO_HUMAN. The API integrates with CRM systems via REST to create and update customer records and support issues, applies safeguards such as failure counters and agent keywords for fast escalation, and is designed as a zero-ops, horizontally scalable platform with an NLP-assisted V2 roadmap using Azure OpenAI for intent classification and entity extraction under strict safety guardrails.",
    technologies: [
      "C#",
      "Azure Functions",
      "Azure Cosmos DB (NoSQL)",
      "WhatsApp Business API",
      "Finite State Machine (FSM) Conversation Engine",
      "Event-Driven Serverless Architecture",
      "REST API Integration",
      "Session Management & Cooldown Logic",
      "Deterministic Menu-Driven Flows",
      "Azure OpenAI",
    ],
    executiveSummary: "/projects/support-automation/summary",
  },
  {
    title: "Jira Service Management Automation",
    subtitle:
      "Jira API + AWS Lambda + API Gateway | Serverless Automation Pipeline",
    summary:
      "Developed a serverless automation framework that integrates Jira Service Management with AWS Lambda and API Gateway to streamline ticket creation, workflow execution, and CRM logging. The system leverages event-driven orchestration: when a new Jira ticket is created, a POST request triggers API Gateway, which routes to the appropriate Lambda function based on request type. Lambda executes the task, logs results back to Jira via REST API, and updates CRM records for full traceability. Built with OAuth 2.0 for secure authentication and deployed using the Serverless Framework.",
    technologies: [
      "Serverless Framework",
      "AWS Lambda",
      "AWS API Gateway",
      "CloudWatch",
      "AWS IAM",
      "JIRA Service Management (ITSM)",
      "JIRA REST API",
      "OAuth 2.0",
      "CRM Integration",
      "DevOps Workflow Automation",
    ],
    executiveSummary: "/projects/jsm-automation/summary",
    systemArchitectureLink: "/projects/jsm-automation/architecture",
  },
  {
    title: "AI Movie Recommendation Agent",
    subtitle:
      "Cloudflare Workers + LLM + ReactJs | Context-Aware Movie Recommendation Backend",
    summary:
      "A lightweight Cloudflare Workers backend that powers the Movie Recommendation Assistant. Guided by three discovery questions, the API turns a small structured payload into a personalized movie pick by orchestrating validation, an LLM call, and response shaping. The service exposes a single POST /api/recommend endpoint, validates input against predefined answer maps, then passes the user’s emotional needs, mental energy, and desired experience into an LLM integration layer to generate a tailored recommendation payload.",
    technologies: [
      "Cloudflare Workers",
      "Node.js",
      "REST API",
      "LLM Integration Layer",
      "Input Validation & Error Handling",
      "Data modeling",
      "Vitest",
      "Wrangler",
      "Rate Limiting & throttling",
      "Serverless Architecture",
    ],
    githubRepository:
      "https://github.com/jackaguilera/movie-recommendation-api",
    executiveSummary: "/projects/movie-recommendation/summary",
  },
  {
    title: "AI-Powered Personal Finance Platform",
    subtitle: "Django + React + AWS | Cloud-Native AI Platform",
    summary:
      "Designed and implemented a full-stack financial management platform delivering real-time analytics and personalized savings recommendations through AI-driven expense classification. The React frontend communicates with a Django REST API hosted in AWS Fargate, processing transaction data with Scikit-learn for intelligent expense prediction and classification. Real-time user metrics are visualized through Chart.js and Recharts, while all traffic flows through API Gateway with OAuth 2.0 and 2FA (TOTP) authentication. The entire infrastructure is managed via CloudFormation for infrastructure-as-code, with services containerized in Docker and deployed through automated CI/CD pipelines.",
    technologies: [
      "Django REST Framework",
      "React",
      "AWS Fargate",
      "AWS RDS",
      "AWS Elasticache (Redis)",
      "AWS API Gateway",
      "Docker",
      "CloudFormation",
      "scikit-learn",
      "OAuth 2.0 + TOTP 2FA",
    ],
    executiveSummary: "/projects/finance-platform/summary",
    systemArchitectureLink: "/projects/finance-platform/architecture",
  },
  {
    title: "Real-Time Auction Platform",
    subtitle:
      "Node.js + React + GraphQL + AWS | Event-Driven Auction Infrastructure",
    summary:
      "Developed a full-stack social auction platform enabling real-time bidding, direct peer-to-peer transactions, and live collection updates for watch enthusiasts. The React frontend connects to a Node.js + Express backend with Apollo GraphQL, deployed on AWS with real-time bid updates broadcasted via MQTT through AWS IoT Core. Stripe API handles secure payment processing, while data persistence uses RDS (MySQL) with Redis (ElastiCache) for performance optimization. Services are containerized with Docker and orchestrated via ECS, with CI/CD automation provided by GitHub Actions and infrastructure management through CloudFormation.",
    technologies: [
      "Node.js",
      "Express.js",
      "React",
      "Apollo GraphQL",
      "AWS IoT Core (MQTT)",
      "Stripe API",
      "Amazon RDS",
      "Redis (ElastiCache)",
      "Docker",
      "AWS ECS",
      "CI/CD pipelines",
    ],
  },
  {
    title: "Dynamic Storage and Retrieval System",
    subtitle:
      "Django + Vue.js + MySQL + AWS | Modular Asset Management Platform",
    summary:
      "Developed a modular storage and retrieval system supporting flexible check-in/check-out workflows across multiple object types, originally architected for valet operations and later adapted for logistics and inventory management. The Vue.js frontend interacts with a Django REST backend hosted on AWS EC2, persisting data in RDS MySQL. The system uses JWT authentication with RBAC (Role-Based Access Control) to enforce security and access policies. A key architectural feature is the dynamic admin blueprint system, which allows creation of new storage workflows and schemas without backend code modifications, enabling rapid iteration and customization for different business domains.",
    technologies: [
      "Django REST Framework",
      "Vue.js",
      "AWS EC2",
      "AWS RDS",
      "JWT Authentication",
      "RBAC (Role-Based Access Control)",
      "RESTful API Design",
      "Multi-tenant architecture",
      "Admin Blueprint System",
    ],
    executiveSummary: "/projects/dynamic-storage/summary",
    systemArchitectureLink: "/projects/dynamic-storage/architecture",
  },
  {
    title: "Cloud-Based E-Commerce & Lead Capture Platform",
    subtitle:
      "Shopify + HubSpot + Cloudflare + Oracle Cloud | Cloud-Native Marketing Ecosystem",
    summary:
      "Implemented a multi-service e-commerce and marketing ecosystem integrating Shopify for product sales, HubSpot for CRM automation and lead tracking, and a distributed backend across Cloudflare and Oracle Cloud. The architecture operates on an OpEx model: Shopify manages transactions and inventory, HubSpot handles marketing automation and lead lifecycle, while the frontend marketing website is hosted on Cloudflare with serverless REST APIs deployed to Workers. Cloudflare provides DNS, HTTPS, and CDN caching for global performance, while Zoho Mail supports secure team communication. This decoupled approach allows flexible scaling and cost optimization.",
    technologies: [
      "Shopify",
      "HubSpot (CRM)",
      "Cloudflare Workers",
      "Cloudflare CDN & DNS",
      "Oracle Cloud infrastructure",
      "REST API",
      "Zoho Mail",
      "Cloud-Native Architecture",
      "Microservices",
    ],
  },
];
